import axios from 'axios';

// API endpoint configuration
const ENDPOINTS = {
  local: 'http://localhost:8001',
  production: import.meta.env.VITE_API_URL || 'https://dev.askquorum.io'
};

const STORAGE_KEY = 'quorum_api_endpoint';

// Get the current API endpoint from localStorage or use production default
export const getApiEndpoint = () => {
  return localStorage.getItem(STORAGE_KEY) || 'production';
};

// Set the API endpoint and reload to apply changes
export const setApiEndpoint = (endpoint) => {
  if (endpoint !== 'local' && endpoint !== 'production') {
    throw new Error('Invalid endpoint. Must be "local" or "production"');
  }
  localStorage.setItem(STORAGE_KEY, endpoint);
  window.location.reload();
};

// Get the actual URL for current endpoint
export const getApiUrl = () => {
  const endpoint = getApiEndpoint();
  return ENDPOINTS[endpoint];
};

// Check if using local endpoint
export const isLocalEndpoint = () => getApiEndpoint() === 'local';

const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
  refresh: async (refreshToken) => {
    const response = await api.post('/api/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  }
};

// Admin APIs
export const adminAPI = {
  // Dashboard
  getDashboard: async () => {
    const response = await api.get('/api/admin/dashboard');
    return response.data;
  },

  // Users
  getUsers: async ({ skip = 0, limit = 50, search = '', plan = '' } = {}) => {
    const params = new URLSearchParams({ skip, limit });
    if (search) params.append('search', search);
    if (plan) params.append('plan', plan);
    const response = await api.get(`/api/admin/users?${params}`);
    return response.data;
  },
  getUserCount: async ({ search = '', plan = '' } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (plan) params.append('plan', plan);
    const response = await api.get(`/api/admin/users/count?${params}`);
    return response.data;
  },
  getUser: async (userId) => {
    const response = await api.get(`/api/admin/users/${userId}`);
    return response.data;
  },
  updateUser: async (userId, data) => {
    const response = await api.patch(`/api/admin/users/${userId}`, data);
    return response.data;
  },
  resetPassword: async (userId, newPassword) => {
    const response = await api.post(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword });
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/admin/users/${userId}`);
    return response.data;
  },
  getUserSecrets: async (userId) => {
    const response = await api.get(`/api/admin/users/${userId}/secrets`);
    return response.data;
  },
  updateSubscription: async (userId, plan, status) => {
    const data = { plan };
    if (status) data.status = status;
    const response = await api.patch(`/api/admin/users/${userId}/subscription`, data);
    return response.data;
  },

  // Usage (basic query stats)
  getUsageStats: async ({ skip = 0, limit = 50 } = {}) => {
    const response = await api.get(`/api/admin/usage?skip=${skip}&limit=${limit}`);
    return response.data;
  },
  getUserUsageHistory: async (userId, months = 12) => {
    const response = await api.get(`/api/admin/usage/${userId}/history?months=${months}`);
    return response.data;
  },

  // Token Analytics
  getTokenSummary: async () => {
    const response = await api.get('/api/admin/tokens/summary');
    return response.data;
  },
  getTokensByModel: async (days = 30) => {
    const response = await api.get(`/api/admin/tokens/by-model?days=${days}`);
    return response.data;
  },
  getDailyTokenUsage: async (days = 30) => {
    const response = await api.get(`/api/admin/tokens/daily?days=${days}`);
    return response.data;
  },
  getUserTokenStats: async ({ skip = 0, limit = 50, sortBy = 'total_tokens' } = {}) => {
    const response = await api.get(`/api/admin/tokens/users?skip=${skip}&limit=${limit}&sort_by=${sortBy}`);
    return response.data;
  },
  getUserTokenDetails: async (userId, months = 12) => {
    const response = await api.get(`/api/admin/tokens/users/${userId}?months=${months}`);
    return response.data;
  },
  getTokenRequests: async ({ skip = 0, limit = 50, userId = '', requestType = '', hours = null } = {}) => {
    const params = new URLSearchParams({ skip, limit });
    if (userId) params.append('user_id', userId);
    if (requestType) params.append('request_type', requestType);
    if (hours) params.append('hours', hours);
    const response = await api.get(`/api/admin/tokens/requests?${params}`);
    return response.data;
  },
  getTokensByType: async (days = 30) => {
    const response = await api.get(`/api/admin/tokens/by-type?days=${days}`);
    return response.data;
  },

  // Error Logs
  getErrors: async ({ skip = 0, limit = 50, category = '', unresolvedOnly = false, hours = null } = {}) => {
    const params = new URLSearchParams({ skip, limit });
    if (category) params.append('category', category);
    if (unresolvedOnly) params.append('unresolved_only', 'true');
    if (hours) params.append('hours', hours);
    const response = await api.get(`/api/admin/errors?${params}`);
    return response.data;
  },
  getErrorStats: async (hours = 24) => {
    const response = await api.get(`/api/admin/errors/stats?hours=${hours}`);
    return response.data;
  },
  getErrorDetail: async (errorId) => {
    const response = await api.get(`/api/admin/errors/${errorId}`);
    return response.data;
  },
  resolveError: async (errorId, notes = '') => {
    const response = await api.post(`/api/admin/errors/${errorId}/resolve`, null, {
      params: notes ? { notes } : {}
    });
    return response.data;
  },
  deleteError: async (errorId) => {
    const response = await api.delete(`/api/admin/errors/${errorId}`);
    return response.data;
  },

  // Models
  getModels: async ({ includeAll = true } = {}) => {
    const params = new URLSearchParams();
    // include_deprecated=true returns ALL models (active, inactive, and deprecated)
    if (includeAll) params.append('include_deprecated', 'true');
    const response = await api.get(`/api/models/v2?${params}`);
    return response.data;
  },
  getModel: async (modelId) => {
    // Note: model IDs contain slashes (e.g., "google/gemini-2.5-flash")
    // Backend uses {model_id:path} to handle this, so we don't URL-encode
    const response = await api.get(`/api/models/${modelId}`);
    return response.data;
  },
  updateModel: async (modelId, data) => {
    const response = await api.patch(`/api/models/${modelId}`, data);
    return response.data;
  },
  createModel: async (data) => {
    const response = await api.post('/api/models', data);
    return response.data;
  },
  deleteModel: async (modelId) => {
    const response = await api.delete(`/api/models/${modelId}`);
    return response.data;
  },
  activateModel: async (modelId) => {
    const response = await api.post(`/api/models/${modelId}/activate`);
    return response.data;
  },
  deactivateModel: async (modelId) => {
    const response = await api.post(`/api/models/${modelId}/deactivate`);
    return response.data;
  },
  deprecateModel: async (modelId, message = '') => {
    const response = await api.post(`/api/models/${modelId}/deprecate`, null, {
      params: message ? { message } : {}
    });
    return response.data;
  },
  getModelRateLimits: async (modelId) => {
    const response = await api.get(`/api/models/${modelId}/rate-limits`);
    return response.data;
  },
  updateModelRateLimits: async (modelId, data) => {
    const response = await api.patch(`/api/models/${modelId}/rate-limits`, data);
    return response.data;
  },
  getModelsCount: async () => {
    const response = await api.get('/api/models/count');
    return response.data;
  },
  getProviders: async () => {
    const response = await api.get('/api/models/providers');
    return response.data;
  },

  // Monitoring
  getMonitoringSettings: async () => {
    const response = await api.get('/api/admin/monitoring/settings');
    return response.data;
  },
  updateMonitoringSettings: async (settings) => {
    const response = await api.patch('/api/admin/monitoring/settings', settings);
    return response.data;
  },
  getMonitoringStatus: async () => {
    const response = await api.get('/api/admin/monitoring/status');
    return response.data;
  },

  // Memory Management
  getMemoryConfig: async () => {
    const response = await api.get('/api/admin/memory/config');
    return response.data;
  },
  updateMemoryConfig: async (config) => {
    const response = await api.patch('/api/admin/memory/config', config);
    return response.data;
  },
  getMemoryStats: async () => {
    const response = await api.get('/api/admin/memory/stats');
    return response.data;
  },
  getUserMemories: async (userId, { activeOnly = true, limit = 100 } = {}) => {
    const params = new URLSearchParams({ active_only: activeOnly, limit });
    const response = await api.get(`/api/admin/memory/users/${userId}/memories?${params}`);
    return response.data;
  },
  createUserMemory: async (userId, memoryData) => {
    const response = await api.post(`/api/admin/memory/users/${userId}/memories`, {
      user_id: userId,
      ...memoryData
    });
    return response.data;
  },
  deleteMemory: async (memoryId, hardDelete = false) => {
    const params = hardDelete ? '?hard_delete=true' : '';
    const response = await api.delete(`/api/admin/memory/memories/${memoryId}${params}`);
    return response.data;
  },
  cleanupExpiredMemories: async () => {
    const response = await api.post('/api/admin/memory/cleanup/expired');
    return response.data;
  },
  deleteConversationSummary: async (conversationId) => {
    const response = await api.delete(`/api/admin/memory/summaries/${conversationId}`);
    return response.data;
  },

  // Semantic Search / Embeddings
  getEmbeddingsStatus: async () => {
    const response = await api.get('/api/admin/memory/embeddings/status');
    return response.data;
  },
  generateUserEmbeddings: async (userId, force = false) => {
    const params = force ? '?force=true' : '';
    const response = await api.post(`/api/admin/memory/embeddings/generate/${userId}${params}`);
    return response.data;
  },
  generateAllEmbeddings: async (force = false) => {
    const params = force ? '?force=true' : '';
    const response = await api.post(`/api/admin/memory/embeddings/generate-all${params}`);
    return response.data;
  },

  // Conversation Summaries (lazy loading)
  getConversationStats: async () => {
    const response = await api.get('/api/admin/conversations/stats');
    return response.data;
  },
  getUsersWithConversations: async ({ search = '', limit = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.append('search', search);
    const response = await api.get(`/api/admin/conversations/users?${params}`);
    return response.data;
  },
  getUserConversations: async (userId, { search = '', limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.append('search', search);
    const response = await api.get(`/api/admin/conversations/users/${userId}/conversations?${params}`);
    return response.data;
  },
  getConversationSummary: async (conversationId) => {
    const response = await api.get(`/api/admin/conversations/${conversationId}/summary`);
    return response.data;
  },
  getConversationDetail: async (conversationId, includeMessages = true) => {
    const params = new URLSearchParams({ include_messages: includeMessages });
    const response = await api.get(`/api/admin/conversations/${conversationId}?${params}`);
    return response.data;
  },

  // Intelligence Domain
  getIntelligenceSettings: async () => {
    const response = await api.get('/api/admin/intelligence/settings');
    return response.data;
  },
  updateIntelligenceSettings: async (settings) => {
    const response = await api.patch('/api/admin/intelligence/settings', settings);
    return response.data;
  },
  getIntelligenceStats: async () => {
    const response = await api.get('/api/admin/intelligence/stats');
    return response.data;
  },
  getAvailableModelsForPriority: async () => {
    const response = await api.get('/api/admin/intelligence/available-models');
    return response.data;
  },
  getQueryLogs: async ({ skip = 0, limit = 50, route_type = '', hours = 24 } = {}) => {
    const params = new URLSearchParams({ skip, limit, hours });
    if (route_type) params.append('route_type', route_type);
    const response = await api.get(`/api/admin/intelligence/logs?${params}`);
    return response.data;
  },

  // Token Budget Config
  getTokenBudgets: async () => {
    const response = await api.get('/api/admin/token-budgets');
    return response.data;
  },
  updateTokenBudgets: async (config) => {
    const response = await api.patch('/api/admin/token-budgets', config);
    return response.data;
  },

  // Consensus Config
  getConsensusConfig: async () => {
    const response = await api.get('/api/admin/consensus-config');
    return response.data;
  },
  updateConsensusConfig: async (config) => {
    const response = await api.patch('/api/admin/consensus-config', config);
    return response.data;
  },

  // Subscription Config
  getSubscriptionConfig: async () => {
    const response = await api.get('/api/admin/subscription-config');
    return response.data;
  },
  updateSubscriptionConfig: async (config) => {
    // Transform nested config to flat fields expected by backend
    const flatConfig = {
      free_queries_per_month: config.free?.queries_per_month,
      free_council_model_limit: config.free?.council_model_limit,
      pro_go_queries_per_month: config.professional_go?.queries_per_month,
      pro_go_council_model_limit: config.professional_go?.council_model_limit,
      pro_go_price_usd: config.professional_go?.price_usd,
      pro_go_price_inr: config.professional_go?.price_inr,
      pro_max_queries_per_month: config.professional_max?.queries_per_month,
      pro_max_council_model_limit: config.professional_max?.council_model_limit,
      pro_max_price_usd: config.professional_max?.price_usd,
      pro_max_price_inr: config.professional_max?.price_inr,
      enterprise_queries_per_month: config.enterprise?.queries_per_month,
      enterprise_council_model_limit: config.enterprise?.council_model_limit,
    };
    const response = await api.patch('/api/admin/subscription-config', flatConfig);
    return response.data;
  },

  // Council Config
  getCouncilConfig: async () => {
    const response = await api.get('/api/admin/council-config');
    return response.data;
  },
  updateCouncilConfig: async (config) => {
    const response = await api.patch('/api/admin/council-config', config);
    return response.data;
  }
};

// AskQuorumGrafana API (for metrics dashboard)
const GRAFANA_ENDPOINTS = {
  local: 'http://localhost:8002',
  production: import.meta.env.VITE_GRAFANA_URL || 'https://askquorum-metrics.fly.dev'
};

const grafanaApi = axios.create({
  baseURL: GRAFANA_ENDPOINTS[getApiEndpoint()],
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and API key to grafana requests
grafanaApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add Grafana API key for authentication
  const grafanaApiKey = import.meta.env.VITE_GRAFANA_API_KEY;
  if (grafanaApiKey) {
    config.headers['X-API-Key'] = grafanaApiKey;
  }
  return config;
});

export const grafanaAPI = {
  // Health check
  getHealth: async () => {
    const response = await grafanaApi.get('/health');
    return response.data;
  },

  // Metrics
  listMetrics: async () => {
    const response = await grafanaApi.get('/api/metrics/list');
    return response.data;
  },
  getSummary: async () => {
    const response = await grafanaApi.get('/api/metrics/summary');
    return response.data;
  },
  queryMetrics: async (metricName, duration = '1h', aggregation = 'avg') => {
    const response = await grafanaApi.get('/api/metrics/query', {
      params: { metric_name: metricName, duration, aggregation }
    });
    return response.data;
  },
  queryMetricsWithLabels: async (metricName, duration = '1h', aggregation = 'avg', labels = {}) => {
    const response = await grafanaApi.post('/api/metrics/query', {
      metric_name: metricName,
      duration,
      aggregation,
      labels
    });
    return response.data;
  },
  getCurrentValue: async (metricName) => {
    const response = await grafanaApi.get(`/api/metrics/current/${metricName}`);
    return response.data;
  },
  getMetricInfo: async (metricName) => {
    const response = await grafanaApi.get(`/api/metrics/info/${metricName}`);
    return response.data;
  },
  getCollectorStatus: async () => {
    const response = await grafanaApi.get('/api/metrics/collector/status');
    return response.data;
  },
  triggerCollection: async () => {
    const response = await grafanaApi.post('/api/metrics/collector/collect');
    return response.data;
  },
  getStorageStats: async () => {
    const response = await grafanaApi.get('/api/metrics/storage/stats');
    return response.data;
  },

  // Dashboards
  listDashboards: async () => {
    const response = await grafanaApi.get('/api/dashboards');
    return response.data;
  },
  getDashboard: async (id) => {
    const response = await grafanaApi.get(`/api/dashboards/${id}`);
    return response.data;
  },
  getDefaultDashboard: async () => {
    const response = await grafanaApi.get('/api/dashboards/default');
    return response.data;
  },
  createDashboard: async (data) => {
    const response = await grafanaApi.post('/api/dashboards', data);
    return response.data;
  },
  updateDashboard: async (id, data) => {
    const response = await grafanaApi.patch(`/api/dashboards/${id}`, data);
    return response.data;
  },
  deleteDashboard: async (id) => {
    const response = await grafanaApi.delete(`/api/dashboards/${id}`);
    return response.data;
  },
  addPanel: async (dashboardId, panelData) => {
    const response = await grafanaApi.post(`/api/dashboards/${dashboardId}/panels`, panelData);
    return response.data;
  },
  deletePanel: async (panelId) => {
    const response = await grafanaApi.delete(`/api/dashboards/panels/${panelId}`);
    return response.data;
  }
};

export default api;
